// src/pages/IntakeForm.js
// HomeIQa — Property Intake Form (Step 1 of purchase flow)
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import ProgressBar from '../components/ProgressBar';

const STEPS = [
  { id: 1, question: "What's the property address?", field: 'address', type: 'address', placeholder: 'Start typing your address...' },
  { id: 2, question: "What year did you purchase this home?", field: 'purchaseYear', type: 'number', placeholder: 'e.g. 2018', min: 1900, max: 2026 },
  { id: 3, question: "What did you pay for the home?", field: 'purchasePrice', type: 'currency', placeholder: 'e.g. 310000' },
  { id: 4, question: "Approximately how large is the home?", field: 'sqft', type: 'number', placeholder: 'Square footage — e.g. 1850' },
  { id: 5, question: "How many bedrooms and bathrooms?", field: 'bedsBaths', type: 'bedsbaths' },
  { id: 6, question: "How would you describe the current condition?", field: 'condition', type: 'select', options: ['Excellent — recently renovated', 'Good — well maintained, minor updates', 'Fair — needs some work', 'Poor — significant repairs needed'] },
  { id: 7, question: "What type of property is this?", field: 'propertyType', type: 'select', options: ['Single family home', 'Townhouse', 'Condo / apartment', 'Multi-family (2-4 units)', 'Other'] },
  { id: 8, question: "What's your primary goal right now?", field: 'goal', type: 'select', options: ['Thinking about selling in the next 6 months', 'Exploring my options — no rush', 'Curious about my equity position', 'Requesting an official appraisal', 'PMI removal or refinancing'] },
  { id: 9, question: "What add-on reports would you like?", field: 'addons', type: 'multiselect', options: [{ label: 'Crime, schools & livability', price: 9 }, { label: 'Flood & climate risk', price: 12 }, { label: 'Permit & lien history', price: 14 }] },
  { id: 10, question: "Where should we send your report?", field: 'email', type: 'email', placeholder: 'your@email.com' }
];

export default function IntakeForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    address: '', purchaseYear: '', purchasePrice: '', sqft: '',
    beds: '3', baths: '2', condition: '', propertyType: '',
    goal: '', addons: [], email: '', firstName: ''
  });
  const [error, setError] = useState('');
  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Google Places autocomplete on address step
  useEffect(() => {
    if (step === 0 && addressRef.current && window.google) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, address: place.formatted_address }));
        }
      });
    }
  }, [step]);

  const currentStep = STEPS[step];
  const progress = ((step) / STEPS.length) * 100;

  function validate() {
    const val = formData[currentStep.field];
    if (currentStep.type === 'multiselect') return true; // optional
    if (currentStep.type === 'bedsbaths') return true;
    if (!val || (typeof val === 'string' && val.trim() === '')) {
      setError('Please fill this in before continuing.');
      return false;
    }
    if (currentStep.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val)) {
        setError('Please enter a valid email address.');
        return false;
      }
    }
    setError('');
    return true;
  }

  function next() {
    if (!validate()) return;
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      // Save to sessionStorage and go to checkout
      sessionStorage.setItem('homeIQa_intake', JSON.stringify(formData));
      navigate('/checkout');
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter') next();
  }

  function toggleAddon(label) {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(label)
        ? prev.addons.filter(a => a !== label)
        : [...prev.addons, label]
    }));
  }

  function formatCurrency(val) {
    const num = val.replace(/[^0-9]/g, '');
    return num ? `$${parseInt(num).toLocaleString()}` : '';
  }

  const totalAddons = STEPS[8].options
    .filter(o => formData.addons.includes(o.label))
    .reduce((sum, o) => sum + o.price, 0);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <Logo />
        <div style={styles.stepCount}>Step {step + 1} of {STEPS.length}</div>
      </div>

      <ProgressBar pct={progress} />

      <div style={styles.formCard}>
        <div style={styles.question}>{currentStep.question}</div>

        {/* ADDRESS */}
        {currentStep.type === 'address' && (
          <input
            ref={addressRef}
            style={styles.input}
            value={formData.address}
            onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
            onKeyDown={handleKey}
            placeholder={currentStep.placeholder}
            autoFocus
          />
        )}

        {/* TEXT / NUMBER */}
        {(currentStep.type === 'number') && (
          <input
            style={styles.input}
            type="number"
            value={formData[currentStep.field]}
            onChange={e => setFormData(p => ({ ...p, [currentStep.field]: e.target.value }))}
            onKeyDown={handleKey}
            placeholder={currentStep.placeholder}
            min={currentStep.min}
            max={currentStep.max}
            autoFocus
          />
        )}

        {/* CURRENCY */}
        {currentStep.type === 'currency' && (
          <input
            style={styles.input}
            type="text"
            value={formData[currentStep.field] ? formatCurrency(formData[currentStep.field]) : ''}
            onChange={e => setFormData(p => ({
              ...p,
              [currentStep.field]: e.target.value.replace(/[^0-9]/g, '')
            }))}
            onKeyDown={handleKey}
            placeholder="$000,000"
            autoFocus
          />
        )}

        {/* EMAIL */}
        {currentStep.type === 'email' && (
          <div>
            <input
              style={styles.input}
              type="text"
              value={formData.firstName}
              onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
              placeholder="Your first name"
              autoFocus
            />
            <input
              style={{ ...styles.input, marginTop: 12 }}
              type="email"
              value={formData.email}
              onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
              onKeyDown={handleKey}
              placeholder="your@email.com"
            />
          </div>
        )}

        {/* SELECT */}
        {currentStep.type === 'select' && (
          <div style={styles.optionGrid}>
            {currentStep.options.map(opt => (
              <button
                key={opt}
                style={{
                  ...styles.optionBtn,
                  ...(formData[currentStep.field] === opt ? styles.optionBtnActive : {})
                }}
                onClick={() => {
                  setFormData(p => ({ ...p, [currentStep.field]: opt }));
                  setError('');
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        )}

        {/* BEDS / BATHS */}
        {currentStep.type === 'bedsbaths' && (
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={styles.subLabel}>Bedrooms</div>
              <div style={styles.counterRow}>
                {['1','2','3','4','5','6+'].map(n => (
                  <button key={n}
                    style={{ ...styles.counterBtn, ...(formData.beds === n ? styles.counterActive : {}) }}
                    onClick={() => setFormData(p => ({ ...p, beds: n }))}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={styles.subLabel}>Bathrooms</div>
              <div style={styles.counterRow}>
                {['1','1.5','2','2.5','3','4+'].map(n => (
                  <button key={n}
                    style={{ ...styles.counterBtn, ...(formData.baths === n ? styles.counterActive : {}) }}
                    onClick={() => setFormData(p => ({ ...p, baths: n }))}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MULTISELECT ADDONS */}
        {currentStep.type === 'multiselect' && (
          <div>
            {currentStep.options.map(opt => (
              <button
                key={opt.label}
                style={{
                  ...styles.addonBtn,
                  ...(formData.addons.includes(opt.label) ? styles.addonActive : {})
                }}
                onClick={() => toggleAddon(opt.label)}
              >
                <span style={styles.addonLabel}>{opt.label}</span>
                <span style={styles.addonPrice}>+${opt.price}</span>
              </button>
            ))}
            {totalAddons > 0 && (
              <div style={styles.addonTotal}>
                Add-ons selected: <strong>+${totalAddons}</strong>
              </div>
            )}
            <div style={styles.addonNote}>All add-ons are optional. Skip to continue with the core report only.</div>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.btnRow}>
          {step > 0 && (
            <button style={styles.backBtn} onClick={() => setStep(s => s - 1)}>← Back</button>
          )}
          <button style={styles.nextBtn} onClick={next}>
            {step === STEPS.length - 1 ? 'Continue to checkout →' : 'Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#FAF7F2', fontFamily: "'DM Sans', sans-serif" },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#4A1220', borderBottom: '4px solid #C9A84C' },
  stepCount: { fontSize: 13, color: 'rgba(255,255,255,0.5)' },
  formCard: { maxWidth: 560, margin: '48px auto', background: 'white', borderRadius: 12, padding: '40px 44px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  question: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: '#4A1220', marginBottom: 24, lineHeight: 1.25 },
  input: { width: '100%', padding: '14px 16px', border: '1.5px solid rgba(107,30,46,0.15)', borderRadius: 8, fontSize: 16, fontFamily: "'DM Sans', sans-serif", outline: 'none', color: '#1C1C1C' },
  optionGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionBtn: { padding: '13px 18px', border: '1.5px solid rgba(107,30,46,0.15)', borderRadius: 8, background: 'white', fontSize: 14, color: '#5A5A5A', textAlign: 'left', cursor: 'pointer' },
  optionBtnActive: { background: '#F7F0DC', border: '1.5px solid #C9A84C', color: '#4A1220', fontWeight: 500 },
  subLabel: { fontSize: 12, color: '#9A9A9A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' },
  counterRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  counterBtn: { padding: '8px 14px', border: '1.5px solid rgba(107,30,46,0.15)', borderRadius: 6, background: 'white', fontSize: 13, color: '#5A5A5A', cursor: 'pointer' },
  counterActive: { background: '#6B1E2E', color: 'white', border: '1.5px solid #6B1E2E' },
  addonBtn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '14px 18px', border: '1.5px solid rgba(107,30,46,0.15)', borderRadius: 8, background: 'white', fontSize: 14, cursor: 'pointer', marginBottom: 10 },
  addonActive: { background: '#EAF7EE', border: '1.5px solid #1D9E75' },
  addonLabel: { color: '#1C1C1C', fontWeight: 500 },
  addonPrice: { color: '#1A7042', fontWeight: 500, fontSize: 13 },
  addonTotal: { fontSize: 14, color: '#4A1220', padding: '10px 0', fontWeight: 500 },
  addonNote: { fontSize: 12, color: '#9A9A9A', marginTop: 4 },
  error: { color: '#8A1A1A', fontSize: 13, marginTop: 12, padding: '8px 12px', background: '#FEF0F0', borderRadius: 6 },
  btnRow: { display: 'flex', gap: 12, marginTop: 28 },
  nextBtn: { flex: 1, padding: '14px 24px', background: '#6B1E2E', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  backBtn: { padding: '14px 20px', background: 'transparent', border: '1.5px solid rgba(107,30,46,0.2)', borderRadius: 8, fontSize: 14, color: '#5A5A5A', cursor: 'pointer' }
};
